import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Can I please take all my classes online?",
  "description": "",
  "published": "2011-12-08T00:00:00.000Z",
  "redirect_from": ["/blog/can-i-please-take-all-my-classes-online/swizec/3147"],
  "categories": "Artificial intelligence, Education, eLearning, Machine learning, Math, Stanford University, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/12/300px-5th_Floor_Lecture_Hall3.jpg",
        "alt": "English: 5th floor lecture hall at Baruch Coll...",
        "title": "English: 5th floor lecture hall at Baruch Coll..."
      }}></img></p>
    <p>{`As I prepare to study for the subject that has been the bane of my existence for the past two years (by writing this blog post instead) I can't help but draw a comparison between how classes in my real world university are going and the classes I'm taking as part of Stanford's online experiment offering this year.`}</p>
    <p>{`Granted, the medium of a real world `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Lecture_hall",
        "title": "Lecture hall"
      }}>{`lecture hall`}</a>{` and an online set of videos is very different so I might be comparing apples to oranges. But there is something inherently wrong with the fact I have actively been trying to pass a class for two years and failing, whereas I'm getting scores in the 70% and 80% range on both online classes.`}</p>
    <p>{`Even though the subject making my university experience somewhat horrible is `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Probability_and_statistics",
        "title": "Probability and statistics"
      }}>{`probability and statistics`}</a>{`, which is supposed to be the raw basis for both `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Artificial_intelligence",
        "title": "Artificial intelligence"
      }}>{`artificial intelligence`}</a>{` and `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Machine_learning",
        "title": "Machine learning"
      }}>{`machine learning`}</a>{`.`}</p>
    <p>{`Something is wrong with this picture, probably me though.`}</p>
    <p>{`I believe the main difference is one of attitude. You go into most classes at my faculty expecting to struggle and fail, it's just the way it is around here. Failure isn't only an option, it's a way of life. Regardless, failure isn't applauded, it's not even encouraged, in fact it is frowned upon by everyone.`}</p>
    <h2 {...{
      "id": "failure-isnt-encouraged-its-a-given"
    }}>{`Failure isn't encouraged, it's a given`}</h2>
    <p>{`Stranger still, the professors themselves expect people to fail. Not all of them, some are actually quite nice and very good at teaching. For others it almost seems like grading coursework is a final act of irony. The probability and statistics class, for example, considers it a pass if you solve 20% of your homework.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/12/411755916_76a0807797_m1.jpg",
        "alt": "FAILURE",
        "title": "FAILURE"
      }}></img></p>
    <p>{`Imagine this contrived situation, `}{`_`}{`answering 20% correct `}{`_`}{`is success.`}</p>
    <p>{`And of course you're only allowed to take the quizzes once. Because how else are you going to learn? Surely being given a single chance to answer a question, being told that it's wrong (without an explanation as to how) and not being allowed to try again is the best way to learn.`}</p>
    <p>{`Compare this with stanford's online classes - and I wonder if their real world classes are the same - the teaching is so good that after a single listening to the lecture you can get an 80% correct. Sure, you might say they are somewhat easier than the homework/quizzes I'm used to around here, but it's a lot more encouraging.`}</p>
    <p>{`Most of all, getting decent grades and being `}<em parentName="p">{`encouraged`}</em>{` to retake the quizzes until you get 100% and are certain you understand the material ... well it's a much better experience than being explained something once, not getting very satisfying answers to questions and then being hammered into the ground with coursework.`}</p>
    <p>{`I guess the fundamental difference is this: the online classes convey a sense of teaching, the real world classes convey a sense of putting you in your place and jumping hoops.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.pbs.org/mediashift/2011/12/can-35000-people-learn-anything-from-an-online-class339.html"
        }}>{`Can 35,000 People Learn Anything from an Online Class?`}</a>{` (pbs.org)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://r.zemanta.com/?u=http%3A//www.usnews.com/education/blogs/high-school-notes/2011/11/14/idaho-becomes-fourth-state-to-require-online-classes%3Fs_cid%3Drss%3Ahigh-school-notes%3Aidaho-becomes-fourth-state-to-require-online-classes&a=62408570&rid=61d3b5e3-d63b-477b-90f4-b25053c55190&e=fc335af921ace5855315cd443776ac64"
        }}>{`Idaho Becomes Fourth State to Require Online Classes`}</a>{` (usnews.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://strategyprofs.wordpress.com/2011/11/30/model-thinking-and-game-theory-online-classe/"
        }}>{`Model thinking and game theory: more free online classes`}</a>{` (strategyprofs.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.i-programmer.info/news/150-training-a-education/3361-stanfords-free-computer-science-courses.html"
        }}>{`Stanford's Free Computer Science Courses`}</a>{` (i-programmer.info)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://hashcampuslife.wordpress.com/2011/12/04/tech-talk-myedu-online-class-management/"
        }}>{`Tech Talk: myEdu online class management`}</a>{` (hashcampuslife.wordpress.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=61d3b5e3-d63b-477b-90f4-b25053c55190",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      